import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import CountUp from "react-countup";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation } from "swiper/modules";
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";
import "swiper/css";
import "swiper/css/navigation";
import "./AboutUs.css";
import {
  FaEye,
  FaBullseye,
  FaGem,
  FaArrowRight,
  FaFacebookF,
  FaXTwitter,
  FaYoutube,
  FaLinkedinIn,
  FaInstagram,
} from "react-icons/fa6";

import { Link } from "react-router-dom";
import { useAboutContext } from "../../context/About_context";
import Loader from "../../Common/loader/Loader";
import No_img from "../../Assets/No_img.jpg";
import DOMPurify from "dompurify";
import { DynamicIcon, renderTitleWithGradient } from "../../utils/helper";

/* ---------- Scroll Reveal Component (Framer Motion) ---------- */
const Reveal = ({ children, className = "", delay = 0 }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.05 }}
      transition={{
        duration: 0.7,
        delay: delay / 1000,
        ease: [0.16, 1, 0.3, 1],
      }}
      className={`reveal ${className}`.trim()}
    >
      {children}
    </motion.div>
  );
};

/* ---------- Animated Number Counter (React CountUp) ---------- */
const AnimatedNumber = ({ value }) => {
  const numericPart =
    parseInt(
      String(value)
        .replace(/,/g, "")
        .replace(/[^0-9]/g, ""),
      10,
    ) || 0;
  const hasPlus = String(value).includes("+");
  const hasComma = String(value).includes(",");

  return (
    <CountUp
      end={numericPart}
      duration={2}
      separator={hasComma ? "," : ""}
      suffix={hasPlus ? "+" : ""}
      enableScrollSpy
      scrollSpyOnce
    />
  );
};



const AboutUs = () => {
  const { aboutus_loading, aboutus_data, GetAboutUs } = useAboutContext();
  const [lightboxIndex, setLightboxIndex] = useState(-1);

  useEffect(() => {
    GetAboutUs();
  }, []); // eslint-disable-line

  if (
    aboutus_loading &&
    (!aboutus_data || Object.keys(aboutus_data).length === 0)
  ) {
    return <Loader fullScreen={true} text="Loading About Us..." />;
  }

  return (
    <div className="about-page">
      <Helmet>
        <title>About Bharat Buildcon | Bharat Buildcon</title>
      </Helmet>
      {/* Dynamic Background Mesh Orbs */}
      <div className="bg-orb bg-orb--gold bg-orb--1" />
      <div className="bg-orb bg-orb--blue bg-orb--2" />
      <div className="bg-orb bg-orb--gold bg-orb--3" />

      {/* ---------------- BREADCRUMB & HEADER ---------------- */}
      <section className="about-header-section">
        <div className="about-header-container">
          <div className="clean-breadcrumb-wrapper">
            <nav className="about-style-breadcrumb" aria-label="Breadcrumb">
              <Link to="/" className="bc-home-link">
                HOME
              </Link>
              <span className="bc-slash">/</span>
              <span className="bc-active-page">ABOUT US</span>
            </nav>
          </div>

          <Reveal>
            {aboutus_data?.title && (
              <h1 className="about-hero-title">
                {renderTitleWithGradient(aboutus_data.title)}
              </h1>
            )}
          </Reveal>
        </div>
      </section>

      {/* ---------------- MAIN HERO MEDIA BANNER ---------------- */}
      {aboutus_data?.about_banner_image_full_path && (
        <section className="hero-banner-section">
          <Reveal>
            <div className="hero-card-frame">
              <div className="hero-media-wrapper">
                <img
                  src={aboutus_data.about_banner_image_full_path}
                  alt="Bharat Buildcon Exhibition Center"
                  className="hero-main-img"
                />
                <div className="hero-img-overlay" />
                <div className="hero-grid-blueprint" />
              </div>
            </div>
          </Reveal>
        </section>
      )}

      {/* ---------------- LIVE STATS COUNTER BAR ---------------- */}
      {aboutus_data?.aboutus_counts &&
        aboutus_data.aboutus_counts.length > 0 && (
          <section className="stats-bar-section">
            <div className="stats-grid">
              {aboutus_data.aboutus_counts.map((st, idx) => (
                <Reveal key={st.id || idx} delay={idx * 80}>
                  <div className="stat-card">
                    {st.icon &&
                      typeof st.icon === "string" &&
                      st.icon.trim() !== "" && (
                        <div className="stat-icon-wrapper">
                          <DynamicIcon name={st.icon} />
                        </div>
                      )}
                    <div className="stat-details">
                      <h3 className="stat-number">
                        <AnimatedNumber value={st.value || "0"} />
                      </h3>
                      <p className="stat-label">{st.name}</p>
                    </div>
                    <div className="stat-corner-accent" />
                  </div>
                </Reveal>
              ))}
            </div>
          </section>
        )}

      {/* ---------------- WELCOME / ABOUT ZIGZAG SECTION ---------------- */}
      {(aboutus_data?.about || aboutus_data?.journey) && (
        <section className="about-intro-section">
          <div className="about-intro-grid">
            {/* Left Column: Rich Text & Highlights */}
            <Reveal className="slide-left">
              <div className="intro-content">
                <div className="sub-tag">
                  <span className="tag-line" />
                  <span>ABOUT BHARAT BUILDCON</span>
                </div>

                {aboutus_data?.overview_title && (
                  <h2 className="intro-title">{aboutus_data.overview_title}</h2>
                )}

                {aboutus_data?.about && (
                  <div
                    className="intro-about-api-text"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(aboutus_data.about),
                    }}
                  />
                )}

                {aboutus_data?.journey && (
                  <div
                    className="intro-journey-api-text"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(aboutus_data.journey),
                    }}
                  />
                )}

                {/* Social Connect */}
                {(aboutus_data?.fb_link ||
                  aboutus_data?.twitter_link ||
                  aboutus_data?.linkdin_link ||
                  aboutus_data?.insta_link ||
                  aboutus_data?.youtube_link) && (
                  <div className="intro-footer-row">
                    <div className="social-connect">
                      <span className="social-label">Connect:</span>
                      <div className="social-icons">
                        {aboutus_data?.fb_link && (
                          <a
                            href={aboutus_data.fb_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Facebook"
                            title="Facebook"
                            className="fb"
                          >
                            <FaFacebookF />
                          </a>
                        )}
                        {aboutus_data?.twitter_link && (
                          <a
                            href={aboutus_data.twitter_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Twitter X"
                            title="Twitter X"
                            className="tw"
                          >
                            <FaXTwitter />
                          </a>
                        )}
                        {aboutus_data?.linkdin_link && (
                          <a
                            href={aboutus_data.linkdin_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="LinkedIn"
                            title="LinkedIn"
                            className="in"
                          >
                            <FaLinkedinIn />
                          </a>
                        )}
                        {aboutus_data?.insta_link && (
                          <a
                            href={aboutus_data.insta_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Instagram"
                            title="Instagram"
                            className="ig"
                          >
                            <FaInstagram />
                          </a>
                        )}
                        {aboutus_data?.youtube_link && (
                          <a
                            href={aboutus_data.youtube_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="YouTube"
                            title="YouTube"
                            className="yt"
                          >
                            <FaYoutube />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </Reveal>

            {/* Right Column: Layered Media Frame with Swiper Slider or Fallback Image */}
            <Reveal className="slide-right" delay={150}>
              <div className="intro-media-container">
                <div className="media-backdrop-glow" />
                <div className="media-frame-main">
                  {aboutus_data?.about_images &&
                  aboutus_data.about_images.length > 0 ? (
                    <div className="custom-about-slider-wrapper">
                      <Swiper
                        modules={[Autoplay, Navigation]}
                        slidesPerView={1}
                        spaceBetween={0}
                        loop={aboutus_data.about_images.length > 1}
                        speed={700}
                        autoplay={{
                          delay: 3500,
                          disableOnInteraction: false,
                          pauseOnMouseEnter: true,
                        }}
                        className="about-swiper"
                      >
                        {aboutus_data.about_images.map((item, idx) => (
                          <SwiperSlide key={item?.id || idx}>
                            <img
                              src={item?.image_full_path || item?.image}
                              alt={`Bharat Buildcon Exhibition ${idx + 1}`}
                              className="intro-photo"
                              onClick={() => setLightboxIndex(idx)}
                            />
                          </SwiperSlide>
                        ))}
                      </Swiper>
                    </div>
                  ) : (
                    <img
                      src={No_img}
                      alt="Bharat Buildcon About"
                      className="intro-photo"
                    />
                  )}
                  <div className="photo-shade" />
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* ---------------- VISION, MISSION & CORE VALUES ---------------- */}
      {(aboutus_data?.vision ||
        aboutus_data?.mission_vision ||
        (aboutus_data?.core_values && aboutus_data.core_values.length > 0)) && (
        <section className="vmv-dark-section" id="vision-mission">
          <div id="visionmission" />
          <div className="vmv-container">
            <div className="vmv-header">
              <Reveal>
                <h2 className="vmv-title">
                  Vision, Mission &amp; <span>Core Values</span>
                </h2>
              </Reveal>
            </div>

            <div className="vmv-grid">
              {/* Vision Card */}
              {aboutus_data?.vision && (
                <Reveal className="vmv-col-card vision-card">
                  <div className="vmv-top-bar">
                    <div className="vmv-icon-glow">
                      <FaEye />
                    </div>
                  </div>
                  <h3 className="vmv-card-title">Our Vision</h3>
                  <div
                    className="vmv-card-text"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(aboutus_data.vision),
                    }}
                  />
                </Reveal>
              )}

              {/* Mission Card */}
              {aboutus_data?.mission_vision && (
                <Reveal className="vmv-col-card mission-card" delay={120}>
                  <div className="vmv-top-bar">
                    <div className="vmv-icon-glow mission">
                      <FaBullseye />
                    </div>
                  </div>
                  <h3 className="vmv-card-title">Our Mission</h3>
                  <div
                    className="vmv-card-text"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(aboutus_data.mission_vision),
                    }}
                  />
                </Reveal>
              )}
            </div>

            {/* Core Values Section */}
            {aboutus_data?.core_values &&
              aboutus_data.core_values.length > 0 && (
                <div className="core-values-wrapper">
                  <Reveal delay={200}>
                    <div className="values-header">
                      <div className="v-icon">
                        <FaGem />
                      </div>
                      <h3>Our Core Values</h3>
                    </div>

                    <div className="values-grid">
                      {aboutus_data.core_values.map((val, idx) => {
                        const iconVal =
                          typeof val === "object" ? val.image : null;
                        return (
                          <div className="val-card" key={val.id || idx}>
                            {iconVal &&
                              typeof iconVal === "string" &&
                              iconVal.trim() !== "" && (
                                <div className="val-icon">
                                  <DynamicIcon name={iconVal} />
                                </div>
                              )}
                            <div className="val-info">
                              <h4>
                                {typeof val === "object"
                                  ? val.title || val.name || val.value_name || ""
                                  : String(val)}
                              </h4>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </Reveal>
                </div>
              )}
          </div>
        </section>
      )}

      {/* ---------------- WHY EXHIBIT SECTION ---------------- */}
      {aboutus_data?.aboutus_exhibits &&
        aboutus_data.aboutus_exhibits.length > 0 && (
          <section className="why-exhibit-section" id="why-exhibit">
            <div className="why-exhibit-container">
              <div className="why-exhibit-header">
                <Reveal>
                  <h2 className="why-exhibit-title">Why Exhibit?</h2>
                </Reveal>
              </div>

              <div className="why-exhibit-grid">
                {aboutus_data.aboutus_exhibits.map((item, idx) => (
                  <Reveal
                    key={item.id || idx}
                    delay={idx * 80}
                    className="why-card-wrapper"
                  >
                    <div className="why-card">
                      {item.icon &&
                        typeof item.icon === "string" &&
                        item.icon.trim() !== "" && (
                          <div className="why-icon">
                            <DynamicIcon name={item.icon} />
                          </div>
                        )}
                      <p>{item.description}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>
        )}

      {/* ---------------- CALL TO ACTION BUTTONS ---------------- */}
      <section className="cta-buttons-only-section">
        <Reveal>
          <div className="cta-btn-group">
            <button type="button" className="cta-btn primary-btn">
              <span>Explore Exhibition</span>
              <FaArrowRight />
            </button>
            <Link to="/registration" className="cta-btn outline-btn">
              Register as Visitor
            </Link>
          </div>
        </Reveal>
      </section>

      {/* Lightbox Modal for Multiple Images */}
      {aboutus_data?.about_images && aboutus_data.about_images.length > 0 && (
        <Lightbox
          open={lightboxIndex >= 0}
          index={lightboxIndex}
          close={() => setLightboxIndex(-1)}
          slides={aboutus_data.about_images.map((item) => ({
            src: item?.image_full_path || item?.image,
          }))}
          plugins={[Zoom]}
          zoom={{
            maxZoomPixelRatio: 5,
            zoomInMultiplier: 2,
          }}
          animation={{ swipe: 280 }}
        />
      )}
    </div>
  );
};

export default AboutUs;
